<template>
  <div class="container py-30 md:py-50">
    <div id="sr-cart-alert" class="sr-only" aria-live="polite" />

    <div class="mb-30 md:mb-40">
      <h1 class="text-h2-mobile lg:text-h2 font-medium uppercase text-blue">
        {{ heading || "Your Cart" }}
      </h1>
      <p v-if="itemCount" class="mt-5 text-sm font-medium text-gray">
        {{ itemCount }} {{ itemCount === 1 ? "Item" : "Items" }}
      </p>
    </div>

    <!-- Empty state -->
    <div
      v-if="!items.length && !loading"
      class="mx-auto flex w-full max-w-[420px] flex-col items-center text-center"
    >
      <span class="mb-15 block text-h4-mobile lg:text-h4 font-medium text-blue">
        Looks like your cart is empty!
      </span>
      <span class="mb-30 block text-sm text-gray">
        {{ empty_cart_label }}
      </span>

      <a
        href="/collections/all"
        class="button w-full max-w-full flex-[0_0_50px]"
      >
        Shop Now
      </a>

      <a
        v-if="empty_cart_btn1_link"
        :href="empty_cart_btn1_link"
        class="mt-10 button button_b w-full max-w-full flex-[0_0_50px]"
      >
        {{ empty_cart_btn1_label || "Shop Best Sellers" }}
      </a>

      <a
        v-if="empty_cart_btn2_link"
        :href="empty_cart_btn2_link"
        class="mt-10 button button_b w-full max-w-full flex-[0_0_50px]"
      >
        {{ empty_cart_btn2_label || "Shop New Arrivals" }}
      </a>
    </div>

    <!-- Cart with items: 2 columns -->
    <div
      v-else-if="items.length"
      class="grid gap-30 md:gap-40 lg:grid-cols-[1fr_400px]"
    >
      <!-- LEFT: items -->
      <div class="border-t border-blue">
        <cart-item
          v-for="item in items"
          :key="item.viewKey || item.key"
          :item="item"
        />
      </div>

      <!-- RIGHT: upsell + summary in the same box -->
      <aside class="lg:sticky lg:top-30 lg:self-start">
        <div class="border-2 border-blue p-20">
          <div
            v-if="showUpsaleEnabled && computedUpsaleProducts.length"
            class="mb-20 border-b border-blue pb-15"
          >
            <h4 class="mb-15 text-h5-mobile lg:text-h5 font-medium capitalize text-blue">
              {{ upsale_headline }}
            </h4>

            <CarouselKeen
              :key="`page-${items.length}`"
              :slides="1"
              :tablet_slides="1"
              :mobile_slides="1"
              :arrows="true"
              :arrows_side="true"
              :progressbar="false"
              class="w-full px-25"
            >
              <div
                v-for="(product, idx) in computedUpsaleProducts"
                :key="`upsell-${idx}`"
                class="keen-slider__slide"
              >
                <cart-item :item="product" :show-add-to-cart-button="true" />
              </div>
            </CarouselKeen>
          </div>

          <div class="mb-15 flex items-end justify-between text-sm">
            <span class="font-bold uppercase">Total</span>
            <span class="flex items-baseline gap-5">
              <del
                v-if="totalCompareAt"
                class="text-xs text-gray"
                v-text="formatMoney(totalCompareAt)"
              />
              <span class="text-base font-bold text-blue">
                {{ formatMoney(cartData.total_price) }}
              </span>
            </span>
          </div>

          <discount-code :key="items.length" />

          <a href="/checkout" class="mt-10 button w-full max-w-full">
            Checkout
          </a>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
export default {
  name: "CartPage",
};
</script>

<script setup>
import {formatMoney as themeFormatMoney} from "@shopify/theme-currency";
import {onBeforeMount, computed} from "vue";
import {useStore} from "vuex";

const props = defineProps({
  heading: {
    type: String,
    default: "Your Cart",
  },
  empty_cart_label: {
    type: String,
    default: "Add your pet's favorite items to the cart!",
  },
  empty_cart_btn1_label: {
    type: String,
    default: "",
  },
  empty_cart_btn1_link: {
    type: String,
    default: "",
  },
  empty_cart_btn2_label: {
    type: String,
    default: "",
  },
  empty_cart_btn2_link: {
    type: String,
    default: "",
  },
  upsale_headline: {
    type: String,
    default: "You might also like",
  },
  upsale_headline_best_sellers: {
    type: String,
    default: "Best Sellers",
  },
  upsale_products: {
    type: String,
    default: "[]",
  },
  show_upsale: {
    type: [String, Boolean],
    default: false,
  },
});

const store = useStore();

const cartData = computed(() => store.state.cart.cartData);
const loading = computed(() => store.state.cart.loading);
const items = computed(() => (cartData.value ? cartData.value.items : []));
const itemCount = computed(() => cartData.value?.item_count ?? 0);

// Strike total mirrors the cart drawer: per-line baseline is
// final_line_price + total_discount (pre-promo), then take the higher of
// original_line_price / compare_at_price so combined line + cart discounts
// show through. Only render the strike if it exceeds cart.total_price.
const totalCompareAt = computed(() => {
  const finalTotal = Number(cartData.value?.total_price) || 0;
  let strike = 0;
  for (const item of items.value) {
    const allocation = item.selling_plan_allocation || {};
    const qty = Number(item.quantity) || 1;
    const finalLine = Number(item.final_line_price ?? item.final_price * qty) || 0;
    const totalDiscount = Number(item.total_discount) || 0;
    let lineStrike = finalLine + totalDiscount;
    const candidates = [
      allocation.compare_at_price ? allocation.compare_at_price * qty : null,
      item.original_line_price,
      item.compare_at_price ? item.compare_at_price * qty : null,
    ];
    for (const candidate of candidates) {
      const num = Number(candidate);
      if (Number.isFinite(num) && num > lineStrike) {
        lineStrike = num;
        break;
      }
    }
    strike += lineStrike;
  }
  return strike > finalTotal ? strike : 0;
});

const upsaleProducts = (() => {
  try {
    return JSON.parse(props.upsale_products) || [];
  } catch {
    return [];
  }
})();

const computedUpsaleProducts = computed(() =>
  upsaleProducts.filter(product => {
    const existsInCart = items.value.some(
      cartItem => cartItem.handle === product.handle,
    );
    return !existsInCart && product.available !== false;
  }),
);

const showUpsaleEnabled = computed(() => {
  if (typeof props.show_upsale === "boolean") return props.show_upsale;
  return props.show_upsale === "true" || props.show_upsale === true;
});

const formatMoney = value => themeFormatMoney(value, "${{amount}}");

onBeforeMount(() => {
  store.dispatch("cart/initCart");
});
</script>
